import React from 'react';
import CommonPageWrapper from '../CommonPageWrapper';
import {AppBar, Box, Stack, Toolbar, Skeleton} from '@mui/material';
import {
  APP_HEADER_HEIGHT,
  DOCUMENT_TOOLBAR_HEIGHT,
  LEFT_NAVIGATION_WIDTH,
  GREY_70,
  WHITE_100,
  GREEN_NAV,
} from '../../App';
import PropTypes from 'prop-types';

export const EmptyAppBar = () => {
  return (
    <AppBar
      position="fixed"
      sx={{
        height: APP_HEADER_HEIGHT,
        zIndex: (theme) => theme.zIndex.drawer,
        boxShadow: 0,
        backgroundColor: GREEN_NAV,
      }}
    >
      <Toolbar
        sx={{height: '100%', minHeight: APP_HEADER_HEIGHT + '!important'}}
      >
        {/* Just an empty green bar to match AppBarV2 */}
      </Toolbar>
    </AppBar>
  );
};

export const EmptyToolbar = () => {
  const toolbarStyle = {
    height: '100%',
    minHeight: APP_HEADER_HEIGHT + '!important',
    borderBottom: '1px solid #DDE0DE',
    background: WHITE_100,
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'space-between',
    px: 2,
  };

  return (
    <AppBar
      position="fixed"
      sx={{
        boxShadow: 'none',
        backgroundColor: GREY_70,
        marginTop: APP_HEADER_HEIGHT,
        height: 56,
        zIndex: 999,
      }}
    >
      <Toolbar sx={toolbarStyle}>
        {/* Left side - smaller rectangle and longer rectangle */}
        <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
          <Skeleton
            variant="rectangular"
            width={80}
            height={24}
            sx={{borderRadius: '4px', backgroundColor: 'rgba(0, 0, 0, 0.08)'}}
          />
          <Skeleton
            variant="rectangular"
            width={350}
            height={24}
            sx={{borderRadius: '4px', backgroundColor: 'rgba(0, 0, 0, 0.08)'}}
          />
        </Box>

        {/* Right side - download button and status strip */}
        <Box sx={{display: 'flex', alignItems: 'center', gap: 2}}>
          <Skeleton
            variant="rectangular"
            width={200}
            height={24}
            sx={{borderRadius: '4px', backgroundColor: 'rgba(0, 0, 0, 0.08)'}}
          />
          <Skeleton
            variant="rectangular"
            width={80}
            height={32}
            sx={{borderRadius: '4px', backgroundColor: 'rgba(0, 0, 0, 0.08)'}}
          />
        </Box>
      </Toolbar>
    </AppBar>
  );
};

const SkeletonLeftNavigation = () => {
  return (
    <Box
      sx={{
        position: 'fixed',
        left: 0,
        top: `calc(${APP_HEADER_HEIGHT} + ${DOCUMENT_TOOLBAR_HEIGHT})`,
        width: LEFT_NAVIGATION_WIDTH,
        height: `calc(100vh - ${APP_HEADER_HEIGHT} - ${DOCUMENT_TOOLBAR_HEIGHT})`,
        backgroundColor: 'background.paper',
        borderRight: '1px solid #e0e0e0',
        zIndex: 999,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Fixed Header Section */}
      <Box
        sx={{
          px: 3,
          pt: 3,
          pb: 2,
          borderBottom: '1px solid #f0f0f0',
          backgroundColor: 'background.paper',
          flexShrink: 0,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Skeleton
            variant="text"
            width={80}
            height={24}
            sx={{backgroundColor: 'rgba(0, 0, 0, 0.08)'}}
          />
          <Box sx={{display: 'flex', gap: 0.5}}>
            <Skeleton
              variant="rectangular"
              width={32}
              height={32}
              sx={{borderRadius: '4px', backgroundColor: 'rgba(0, 0, 0, 0.08)'}}
            />
            <Skeleton
              variant="rectangular"
              width={32}
              height={32}
              sx={{borderRadius: '4px', backgroundColor: 'rgba(0, 0, 0, 0.08)'}}
            />
            <Skeleton
              variant="rectangular"
              width={32}
              height={32}
              sx={{borderRadius: '4px', backgroundColor: 'rgba(0, 0, 0, 0.08)'}}
            />
          </Box>
        </Box>
      </Box>

      {/* Scrollable Sections Area */}
      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
          px: 3,
          pt: 2,
          pb: 3,
        }}
      >
        {/* Generate skeleton sections */}
        {Array.from({length: 6}).map((_, index) => (
          <Skeleton
            key={index}
            variant="rectangular"
            width="100%"
            height={36}
            sx={{
              borderRadius: '4px',
              mb: 3,
              backgroundColor: 'rgba(0, 0, 0, 0.08)',
            }}
          />
        ))}
      </Box>
    </Box>
  );
};

export const EmptyDocument = ({loading = true}) => {
  const containerStyles = {
    position: 'fixed',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    bgcolor: GREY_70,
    zIndex: 1000,
  };

  const contentWrapperStyle = {
    flexGrow: 1,
    display: 'flex',
    height: '100%',
    px: '1.8675rem',
    marginLeft: LEFT_NAVIGATION_WIDTH,
  };

  if (loading) {
    return (
      <Box sx={containerStyles}>
        <EmptyAppBar />
        <EmptyToolbar />
        <SkeletonLeftNavigation />

        <Box mt={'8rem'} sx={contentWrapperStyle}>
          <CommonPageWrapper>
            <Stack
              direction="column"
              alignItems="center"
              flex={1}
              sx={{position: 'relative'}}
            >
              <Box
                sx={{
                  display: 'grid',
                  flexGrow: 1,
                  borderRadius: '8px',
                  placeItems: 'center',
                  position: 'relative',
                }}
              >
                {/* White page background using original className */}
                <div className="loading-page" />

                {/* Skeleton overlay with text-like content */}
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: 'rgba(255,255,255,0.8)',
                    padding: '46px',
                    zIndex: 1,
                  }}
                >
                  {/* Text-like skeleton content */}
                  <Box sx={{width: '100%', maxWidth: '600px'}}>
                    <Skeleton
                      variant="text"
                      width="60%"
                      height={32}
                      sx={{mb: 2, backgroundColor: 'rgba(0, 0, 0, 0.08)'}}
                    />
                    <Skeleton
                      variant="text"
                      width="100%"
                      height={20}
                      sx={{mb: 1, backgroundColor: 'rgba(0, 0, 0, 0.08)'}}
                    />
                    <Skeleton
                      variant="text"
                      width="90%"
                      height={20}
                      sx={{mb: 1, backgroundColor: 'rgba(0, 0, 0, 0.08)'}}
                    />
                    <Skeleton
                      variant="text"
                      width="75%"
                      height={20}
                      sx={{mb: 2, backgroundColor: 'rgba(0, 0, 0, 0.08)'}}
                    />
                    <Skeleton
                      variant="text"
                      width="100%"
                      height={20}
                      sx={{mb: 1, backgroundColor: 'rgba(0, 0, 0, 0.08)'}}
                    />
                    <Skeleton
                      variant="text"
                      width="85%"
                      height={20}
                      sx={{mb: 1, backgroundColor: 'rgba(0, 0, 0, 0.08)'}}
                    />
                    <Skeleton
                      variant="text"
                      width="95%"
                      height={20}
                      sx={{mb: 2, backgroundColor: 'rgba(0, 0, 0, 0.08)'}}
                    />
                    <Skeleton
                      variant="text"
                      width="70%"
                      height={20}
                      sx={{mb: 1, backgroundColor: 'rgba(0, 0, 0, 0.08)'}}
                    />
                    <Skeleton
                      variant="text"
                      width="100%"
                      height={20}
                      sx={{mb: 1, backgroundColor: 'rgba(0, 0, 0, 0.08)'}}
                    />
                  </Box>
                </Box>
              </Box>
            </Stack>
          </CommonPageWrapper>
        </Box>
      </Box>
    );
  }
};

EmptyDocument.propTypes = {
  loading: PropTypes.bool,
};
